import {
  DestroyRef,
  ElementRef,
  Injectable,
  Signal,
  computed,
  inject,
  signal,
} from '@angular/core';

/**
 * Infobulle partagée par tous les graphiques du rapport, et plomberie
 * navigateur qui va avec.
 *
 * Une seule surface pour six graphiques, exactement comme le document
 * d'origine : c'est ce qui empêche le style de survol de diverger d'un
 * graphique à l'autre, et cela évite six éléments flottants dans le DOM d'une
 * page qui s'imprime.
 */

/** Une ligne de l'infobulle : pastille, libellé, valeur déjà formatée. */
export interface ChartTooltipItem {
  color: string;
  label: string;
  value: string;
}

/** Marge de sécurité au bord de la fenêtre, en pixels. */
const EDGE = 8;
/** Décalage du curseur, pour que l'infobulle ne soit pas sous le pointeur. */
const OFFSET = 14;

@Injectable({ providedIn: 'root' })
export class ChartTooltipService {
  readonly visible = signal(false);
  readonly title = signal('');
  readonly items = signal<readonly ChartTooltipItem[]>([]);

  /** Position du pointeur, seule entrée réelle du calcul de placement. */
  private readonly pointerX = signal(0);
  private readonly pointerY = signal(0);

  /**
   * Encombrement mesuré de la bulle, reporté par le composant après rendu.
   *
   * Le service ne peut pas le mesurer lui-même : le contenu n'est écrit dans le
   * DOM qu'à la passe de rendu suivante, et lire la boîte trop tôt donnerait
   * celle du survol précédent — donc un retournement de bord au mauvais moment.
   */
  private readonly boxWidth = signal(0);
  private readonly boxHeight = signal(0);

  /** Abscisse finale : à droite du curseur, retournée si ça déborde. */
  readonly x: Signal<number> = computed(() => {
    const pointer = this.pointerX();
    const width = this.boxWidth();
    let x = pointer + OFFSET;
    if (x + width > window.innerWidth - EDGE) x = pointer - width - OFFSET;
    return Math.max(EDGE, x);
  });

  /** Ordonnée finale, même règle. */
  readonly y: Signal<number> = computed(() => {
    const pointer = this.pointerY();
    const height = this.boxHeight();
    let y = pointer + OFFSET;
    if (y + height > window.innerHeight - EDGE) y = pointer - height - OFFSET;
    return Math.max(EDGE, y);
  });

  show(title: string, items: readonly ChartTooltipItem[], event: MouseEvent): void {
    this.title.set(title);
    this.items.set(items);
    this.pointerX.set(event.clientX);
    this.pointerY.set(event.clientY);
    this.visible.set(true);
  }

  move(event: MouseEvent): void {
    if (!this.visible()) return;
    this.pointerX.set(event.clientX);
    this.pointerY.set(event.clientY);
  }

  hide(): void {
    this.visible.set(false);
  }

  /** Appelé par `<s2e-chart-tooltip>` après chaque rendu. */
  measure(width: number, height: number): void {
    this.boxWidth.set(width);
    this.boxHeight.set(height);
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   Plomberie navigateur des graphiques

   Ces deux fabriques vivent ici plutôt que dans `chart-geometry.ts`, qui est
   du calcul pur et doit le rester. Trois graphiques mesurent leur largeur et
   trois consultent la requête média « compact » : dupliquer l'observateur
   trois fois serait la seule autre option, la liste des fichiers de ce lot
   n'autorisant pas un module d'infrastructure de plus.
   ───────────────────────────────────────────────────────────────────────── */

/** Seuil « compact » du rapport, repris de `isCompact()`. */
const COMPACT_QUERY = '(max-width: 767px)';

/**
 * Signal vrai sous 768 px de large.
 *
 * Un simple booléen figé à la construction ne suffit pas : la hauteur de trois
 * graphiques en dépend, et l'impression bascule la largeur de page sans
 * recharger. À appeler dans un contexte d'injection.
 */
export function compactViewportSignal(): Signal<boolean> {
  const destroyRef = inject(DestroyRef);
  const media = window.matchMedia(COMPACT_QUERY);
  const compact = signal(media.matches);
  const onChange = (event: MediaQueryListEvent): void => compact.set(event.matches);

  media.addEventListener('change', onChange);
  destroyRef.onDestroy(() => media.removeEventListener('change', onChange));
  return compact.asReadonly();
}

/**
 * Largeur utile de l'hôte, suivie par `ResizeObserver`.
 *
 * Les graphiques cartésiens se dimensionnent en pixels, pas en pourcentage :
 * à l'impression la colonne passe de ~1250 px à 616 px et un SVG figé se ferait
 * rogner, axes décalés par rapport aux barres. Le parent est observé lui aussi
 * parce que c'est lui (`.chart-min--620` et consorts) qui porte la largeur
 * minimale ; l'hôte, tant qu'il n'est pas attaché, mesure zéro et on garde la
 * valeur de repli du document d'origine.
 */
export function chartWidthSignal(fallback: number): Signal<number> {
  const host = inject(ElementRef).nativeElement as HTMLElement;
  const destroyRef = inject(DestroyRef);
  const width = signal(fallback);

  const measure = (): void => {
    const own = host.clientWidth;
    const parent = host.parentElement?.clientWidth ?? 0;
    const measured = own > 0 ? own : parent;
    if (measured > 0) width.set(measured);
  };

  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    if (host.parentElement) observer.observe(host.parentElement);
    destroyRef.onDestroy(() => observer.disconnect());
  }

  measure();
  return width.asReadonly();
}
